import { useEffect, useRef, useState } from 'react'
import { CellularPayPrompt } from './types'
import { RECHECK_INTERVAL_MS } from './use-prompt-view'

// The first retry of a failed first load, doubled on each further failure and
// capped at the ordinary re-check interval.
export const FIRST_RETRY_MS = 5 * 1000

export const retryDelay = (attempt: number): number =>
  Math.min(FIRST_RETRY_MS * 2 ** attempt, RECHECK_INTERVAL_MS)

// The last answer the paywall gave, and for which account and workspace: a member's
// lock belongs to one workspace, so it is never carried over to another.
interface Known {
  key: string
  prompt: CellularPayPrompt | null
}

interface UseKnownPromptArgs {
  // Account and workspace the answer is about.
  key: string
  // The query's answer: a prompt, null for "nothing owed", or undefined when there
  // is no answer (skipped, still loading, or failed).
  answer: CellularPayPrompt | null | undefined
  failed: boolean
  retry: () => Promise<unknown>
}

// A failed first load is retried with backoff for as long as it keeps failing.
const useRetryFirstLoad = (failing: boolean, retry: () => Promise<unknown>): void => {
  const latestRetry = useRef(retry)
  useEffect(() => {
    latestRetry.current = retry
  })
  useEffect(() => {
    if (!failing) return undefined
    let cancelled = false
    let timer: number | undefined
    const schedule = (attempt: number) => {
      timer = window.setTimeout(() => {
        latestRetry.current().then(() => {
          if (!cancelled) schedule(attempt + 1)
        })
      }, retryDelay(attempt))
    }
    schedule(0)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [failing])
}

// What to show, given that the paywall can fail to answer at any time.
//
// A failed refetch is a transient error, not "nothing owed": the last known prompt
// stays, so a lock is never lifted, and an open payment dialog never unmounted,
// because a background re-check failed.
//
// A first load that keeps failing shows nothing (and is retried): when the paywall
// is down we would rather leave the CMS open than lock every customer out of it.
export const useKnownPrompt = ({ key, answer, failed, retry }: UseKnownPromptArgs) => {
  const [known, setKnown] = useState<Known | null>(null)

  useEffect(() => {
    if (answer !== undefined) setKnown({ key, prompt: answer })
  }, [key, answer])

  const knownHere = known !== null && known.key === key
  const fallback = knownHere ? known.prompt : null
  const prompt = answer !== undefined ? answer : fallback

  useRetryFirstLoad(failed && answer === undefined && !knownHere, retry)

  // The customer has just settled this bill (paid or gave it up): what we knew is
  // out of date, so a failed refresh must not bring the old bill back.
  const forget = (): void => setKnown({ key, prompt: null })

  // Whether the paywall has answered for this key at all (a prompt, or null for
  // "nothing owed"); false while loading, or while a first load keeps failing.
  const answered = answer !== undefined || knownHere

  return { prompt, answered, forget }
}
